<template>
  <lf-primary-button-with-dropdown
    ref="copyDataButtonRef"
    :dropdown-items="copyDropdownOptions"
    :disabled="noOptionsSelected"
    @button:click="copySelectedData"
  >
    <template #button>
      <span>
        {{
          shouldCopyAllData
            ? $t("APPLICATION_STAGES.COPY_ALL_DATA")
            : $t("APPLICATION_STAGES.COPY_DATA")
        }}
      </span>
    </template>
  </lf-primary-button-with-dropdown>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";

import { getObjectKeys } from "@/helpers/common";
import {
  MULTI_RECORD_BLOCKS,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import {
  filterObjectByValue,
  wordsFirstLetterToUpper,
} from "@/helpers/formatting";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { usePromiseWrapper } from "@/hooks/common";

import LfPrimaryButtonWithDropdown from "@/components/ui/buttons/LfPrimaryButtonWithDropdown.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";

import type { WorkflowBlocksData } from "@/models/workflows";

const { t } = useI18n();
const { getters } = useStore();
const copyToClipboard = useClipboardWithMessage();

const copyDataButtonRef = ref<InstanceType<
  typeof LfPrimaryButtonWithDropdown
> | null>(null);

const copyData = ref({
  [WORKFLOW_BLOCKS_IDS.business_info]: true,
  [WORKFLOW_BLOCKS_IDS.personal_info]: true,
  [WORKFLOW_BLOCKS_IDS.additional_business_info]: true,
  [WORKFLOW_BLOCKS_IDS.funding]: true,
});

const copyDataTitles = {
  [WORKFLOW_BLOCKS_IDS.business_info]: t(
    "APPLICATION_STAGES.BUSINESS_INFORMATION"
  ),
  [WORKFLOW_BLOCKS_IDS.personal_info]: t(
    "APPLICATION_STAGES.PERSONAL_INFORMATION"
  ),
  [WORKFLOW_BLOCKS_IDS.additional_business_info]: t(
    "APPLICATION_STAGES.ADDTIONAL_BUSINESS_INFORMATION"
  ),
  [WORKFLOW_BLOCKS_IDS.funding]: t("DEALS.DEAL_PROGRESS.FUNDING_DETAILS"),
} as const;

const noOptionsSelected = computed(() =>
  Object.values(copyData.value).every((value) => !value)
);

const shouldCopyAllData = computed(() =>
  Object.values(copyData.value).every((value) => value)
);

const templateFields = computed<WorkflowBlocksData>(
  () => getters["workflows/templateFields"]
);

const whenFundsNeededOptions = computed<Record<string, string>>(
  () => getters["options/fundsNeededTimes"]
);

const useOfFunds = computed<(id: number) => string>(
  () => getters["options/useOfFunds"]
);

const copyDropdownOptions = computed(() => [
  {
    label: t("APPLICATION_STAGES.PERSONAL_INFORMATION"),
    handler: handleCopyDataSelection(WORKFLOW_BLOCKS_IDS.personal_info),
    componentData: {
      component: LfCheckbox,
      props: {
        name: "personalInformation",
        modelValue: copyData.value[WORKFLOW_BLOCKS_IDS.personal_info],
        value: copyData.value[WORKFLOW_BLOCKS_IDS.personal_info],
      },
    },
  },
  {
    label: t("APPLICATION_STAGES.BUSINESS_INFORMATION"),
    handler: handleCopyDataSelection(WORKFLOW_BLOCKS_IDS.business_info),
    componentData: {
      component: LfCheckbox,
      props: {
        name: "businessInformation",
        modelValue: copyData.value[WORKFLOW_BLOCKS_IDS.business_info],
        value: copyData.value[WORKFLOW_BLOCKS_IDS.business_info],
      },
    },
  },
  {
    label: t("APPLICATION_STAGES.ADDTIONAL_BUSINESS_INFORMATION"),
    handler: handleCopyDataSelection(
      WORKFLOW_BLOCKS_IDS.additional_business_info
    ),
    componentData: {
      component: LfCheckbox,
      props: {
        name: "additionalBusinessInformation",
        modelValue:
          copyData.value[WORKFLOW_BLOCKS_IDS.additional_business_info],
        value: copyData.value[WORKFLOW_BLOCKS_IDS.additional_business_info],
      },
    },
  },
  {
    label: t("DEALS.DEAL_PROGRESS.FUNDING_DETAILS"),
    handler: handleCopyDataSelection(WORKFLOW_BLOCKS_IDS.funding),
    componentData: {
      component: LfCheckbox,
      props: {
        name: "fundingDetails",
        modelValue: copyData.value[WORKFLOW_BLOCKS_IDS.funding],
        value: copyData.value[WORKFLOW_BLOCKS_IDS.funding],
      },
    },
  },
]);

const handleCopyDataSelection = (key: keyof typeof copyData.value) => () => {
  copyData.value[key] = !copyData.value[key];
};

const getFieldData = (blockId: keyof typeof copyData.value) => {
  const defaultData = templateFields.value[blockId];
  if (defaultData) {
    return defaultData;
  }
  return MULTI_RECORD_BLOCKS.includes(blockId) ? [] : {};
};

const { fetchWrapper: copySelectedData } = usePromiseWrapper(async () => {
  const selectedOptions = filterObjectByValue(copyData.value, (val) => val);

  const wrapper = document.createElement("div");

  const formatKey = (key: string) => {
    const prefixesToReplace = [
      "business_",
      "personal_",
      "additional_business_",
      "funding_",
    ];
    const prefixToReplace = prefixesToReplace.find((prefix) =>
      key.startsWith(prefix)
    );

    if (!prefixToReplace) {
      return wordsFirstLetterToUpper(key.replace(/_/g, " "));
    }

    return wordsFirstLetterToUpper(
      key.replace(prefixToReplace, "").replace(/_/g, " ")
    );
  };

  const formatData = (data: unknown, key: string) => {
    if (data === null) {
      return "-";
    }

    if (key === "funding_when_funds_needed" && typeof data === "string") {
      return whenFundsNeededOptions.value[data] ?? data;
    }

    if (key === "funding_use_of_funds" && typeof data === "number") {
      return useOfFunds.value(data) ?? data;
    }

    return data;
  };

  const appendDataToWrapper = (
    data: unknown[] | unknown,
    parentElement: HTMLElement,
    indentationLevel = 1
  ) => {
    if (Array.isArray(data)) {
      data.forEach((item) =>
        appendDataToWrapper(item, parentElement, indentationLevel + 1)
      );
    } else if (typeof data === "object" && data !== null) {
      const container = document.createElement("div");
      container.style.marginLeft = indentationLevel * 10 + "px";
      const dataKeys = getObjectKeys(data);
      for (const key of dataKeys) {
        const item = document.createElement("p");
        if (typeof data[key] === "object" && data[key] !== null) {
          const label = document.createElement("strong");
          label.textContent = `${formatKey(key)}:`;
          item.appendChild(label);
          appendDataToWrapper(data[key], item, indentationLevel); // Recurse with current item as the new parent
        } else {
          item.innerHTML = `<strong>${formatKey(key)}:</strong> ${formatData(
            data[key],
            key
          )}`;
        }
        container.appendChild(item);
      }
      parentElement.appendChild(container);
    }
  };

  getObjectKeys(selectedOptions).forEach(
    (key) => {
      const fieldData = getFieldData(key);
      const title = document.createElement("h3");
      title.textContent = copyDataTitles[key].toUpperCase();
      wrapper.appendChild(title);

      if (isEmpty(fieldData)) {
        return;
      }

      if (Array.isArray(fieldData)) {
        fieldData.forEach((data) => appendDataToWrapper(data, wrapper));
        return;
      }

      appendDataToWrapper(fieldData, wrapper);
    },
    {} as Record<keyof typeof copyData.value, unknown>
  );

  await copyToClipboard(wrapper.innerHTML, {
    isRichText: true,
    success: t("APPLICATION_STAGES.SAVE_DATA_MESSAGE"),
  });
});
</script>
